














import {Component, Mixins, Prop, Ref, Vue, Watch} from 'vue-property-decorator';
import {ActiveProvide} from "@/support/reactive"
import MonitorMixin from "@/design-system/utilities/MonitorMixin"
import UniqueIdMixin from "@/design-system/utilities/UniqueIdMixin"

/**
 * Represents the pipeline as a whole, and expects stages within it. It will take up as much room
 * as it needs, so place it inside a container that will scroll to accommodate this; Workbench is
 * perfect.
 *
 * @status stable
 * @published 4.5.0
 * @changelog 4.5.0 Published.
 */
@Component({
    components: {}
})
export default class JdsPipeline extends Mixins(MonitorMixin, UniqueIdMixin) {
    @ActiveProvide('jds.pipeline.height') pipelineHeight = 0
    @Ref() pipelineEl!: HTMLElement
    @ActiveProvide('jds.pipeline.bus') bus: Vue|null = null
    @ActiveProvide('jds.pipeline.containerEl') providedEl: HTMLElement|null = null

    dragStartX = 0
    dragStartY = 0
    dragDeltaX = 0
    dragDeltaY = 0
    dragObjectWidth = 0
    dragAnimationFrame: number|null = null
    dropTargetId: string|null = null

    dragEdgeThreshold = 20                              // max gap between phantom and edge to invoke auto-scroll
    dragShouldScroll: 'left'|'right'|null = null        // holds state of auto-scroll loop
    dragScrollLastTick: number|null = null              // holds last timestamp of auto-scroll event
    dragScrollMultiplier = 1.5                          // multiplier of auto-scroll speed

    // states passed down to stages and cards
    @ActiveProvide('jds.pipeline.uid') uid = ''
    @ActiveProvide('jds.pipeline.dragging') dragging = false
    @ActiveProvide('jds.pipeline.dragging-cards') draggingCards: string[] = []
    @ActiveProvide('jds.pipeline.selected-cards') selectedCards: string[] = []
    @ActiveProvide('jds.pipeline.is-selecting') isSelecting = false
    @ActiveProvide('jds.pipeline.selected-stage') selectedStage: string|null = null      // used for per-stage selection contexts

    /**
     * Control whether or not the pipeline allows selections to be made. This _must_ be wired up to acknowledge
     * `multiselect-start` and `multiselect-end` events, which should flip this state to true and false, respectively,
     * and are fired by user events managed by the pipeline.
     *
     * You can, naturally, also mutate this externally, such as in response to a select toggle.
     */
    // inbound selection state from the parent consumer, synced by syncSelecting
    @Prop({ type: Boolean, default: false}) selecting!: boolean

    // inbound selections from the parent consumer, synced by syncSelected
    /**
     * Inform the pipeline which cards are selected by passing in a list of card IDs here. If the user
     * makes selection changes within the pipeline, you **must** detect the `multiselect-change` event,
     * update the selected ID list, and pass it in here, as if it were a `v-model`.
     *
     * If `selected` is not updated for `multiselect-change` events, users will be unable to select cards,
     * even if both `selecting` and `can-select` are properly set.
     *
     * @default []
     * @required
     */
    @Prop({ default: () => { return [] }}) selected!: Array<string>

    // Internally, the pipeline cards manage how to handle selection states on their own.
    /**
     * Determine a multiselect mode. Does not control the selecting state directly, which is instead
     * managed by `selecting`.
     */
    @Prop({ type: String, default: 'none'})
    @ActiveProvide('jds.pipeline.select-mode')
    canSelect!: 'none'|'per-stage'

    beforeMount() {
        this.bus = new Vue()

        // record selected cards
        this.bus.$on('cardSelected', ({ cardId, stageId }: { cardId: string, stageId: string }) => {
            this.selectedStage = stageId
            this.$emit('multiselect-change', [... this.selectedCards, cardId])
        })

        // record card selection stage move
        this.bus.$on('cardSelectionMoved', ({ stageId }: {stageId: string}) => {
            this.selectedStage = stageId
        })

        // record deselected cards
        this.bus.$on('cardDeselected', ({ cardId }: { cardId: string }) => {
            const cardToRemove = this.selectedCards.findIndex(selectedId => selectedId === cardId)
            const newSet = [...this.selectedCards]
            newSet.splice(cardToRemove, 1);

            this.$emit('multiselect-change', newSet)

        })

        this.bus.$on('dragStart', ({ offsetX, offsetY, clientX, clientY, id, width }:
                 {offsetX: number,  offsetY: number, clientX: number, clientY: number, id: string, width: number}) => {
            this.$debug('Drag mode engaged', 'DRAG_START')
            this.dragging = true
            this.dragStartX = offsetX
            this.dragStartY = offsetY
            this.dragDeltaX = clientX - this.dragStartX
            this.dragDeltaY = clientY - this.dragStartY
            this.dragObjectWidth = width
            if (this.selectedCards.length) {
                this.draggingCards = this.selectedCards
            } else {
                this.draggingCards.push(id)
            }

            this.findPhantom().style.transform = "translate3d("+this.dragDeltaX+"px,"+this.dragDeltaY+"px, 0px)"

            // add listeners to track movement and cancel default behavior
            window.addEventListener('contextmenu', this.disableContextMenu,{ passive: false })
            document.addEventListener('pointermove', this.handleMove, { passive: false })
            document.addEventListener('pointerup', this.handleDragUp, { passive: false })
            document.addEventListener('pointercancel', this.handleDragUp, { passive: false })
        })

        // record entry into a drop target
        this.bus.$on('dropTargetEnter', ({ targetId }: { targetId: string }) => {
            this.$debug('Entered drop target')
            this.dropTargetId = targetId
        })

        // record leaving a drop target
        this.bus.$on('dropTargetLeave', ({ targetId }: { targetId: string }) => {
            if (this.dropTargetId === targetId) {
                this.$debug('Left drop target')
                this.dropTargetId = null
            }
        })
    }



    multiselectDownCallback(e: KeyboardEvent) {
        if ( e.key === 'Shift' && this.canSelect === 'per-stage') {
            this.startMultiselect()
        }
    }

    multiselectUpCallback(e: KeyboardEvent) {
        if (this.isSelecting) {
            if (e.key === 'Shift') {
                this.endMultiselect()
            }
        }
    }

    startMultiselect() {
        // this.isSelecting = true
        this.$emit('multiselect-start')
    }

    endMultiselect() {
        // this.isSelecting = false
        this.$emit('multiselect-end')
    }

    handleMove(event: PointerEvent) {
        // prevent mobile browsers from attempting pan events
        event.preventDefault()
        event.stopImmediatePropagation();

        if (!this.dragging) return false;
        if (!this.dragAnimationFrame) {
            this.dragDeltaX = event.clientX - this.dragStartX
            this.dragDeltaY = event.clientY - this.dragStartY

            this.dragAnimationFrame = requestAnimationFrame(() => {
                this.findPhantom().style.transform = "translate3d("+this.dragDeltaX+"px,"+this.dragDeltaY+"px, 0px)"

                // edge check - start auto-scroll loops if in vicinity to scroll
                if (this.shouldScrollLeft(event)) {
                    this.dragShouldScroll = 'left'
                    this.scrollLeft()
                } else if (this.shouldScrollRight(event)) {
                    this.dragShouldScroll = 'right'
                    this.scrollRight()
                } else if (this.dragShouldScroll) {
                    this.dragShouldScroll = null
                    this.dragScrollLastTick = null
                }

                this.dragAnimationFrame = null
            });
        }
    }

    handleDragUp(e) {
        if (!this.dragging) return false

        // emit main drop event
        this.$emit('drop', {
            cardIds: this.draggingCards,
            targetId: this.dropTargetId,
        })

        // reset drag
        this.$debug("Ending drag")
        this.dragging = false
        this.draggingCards = []

        // reset autoscroll
        this.dragShouldScroll = null
        this.dragScrollLastTick = null


        // reset listeners
        document.removeEventListener('pointermove', this.handleMove)
        document.removeEventListener('pointerup', this.handleDragUp)
        document.removeEventListener('pointercancel', this.handleDragUp)
        window.removeEventListener('contextmenu', this.disableContextMenu)
    }

    @Watch('selecting', {immediate: true})
    syncSelecting(value: boolean) {
        this.isSelecting = value
    }

    @Watch('selected')
    syncSelected(value: Array<string>) {
        this.selectedCards = value
    }

    // @Watch('dragging')
    // syncDragging(value: boolean) {
    //     document.documentElement.style.overflow = value ? 'hidden' : 'auto'
    //     this.findParent().style.overflow = value ? 'hidden' : 'auto'
    // }

    mounted() {
        this.providedEl = this.pipelineEl
        this.uid = this.$id()
        window.addEventListener('keydown', this.multiselectDownCallback)
        window.addEventListener('keyup', this.multiselectUpCallback)
    }

    beforeDestroy() {
        window.removeEventListener('keydown', this.multiselectDownCallback)
        window.removeEventListener('keyup', this.multiselectUpCallback)
    }

    findPhantom(): HTMLElement {
        return document.getElementById('JdsIdPipelinePhantomEl'+this.$id()) as HTMLElement
    }

    disableContextMenu(e: MouseEvent) {
        e.preventDefault()
    }

    private scrollLeft() {
        if (this.dragShouldScroll === 'left') {
            requestAnimationFrame((tick) => {
                if (this.dragScrollLastTick) {
                    // we're aiming for 1000px/second * multiplier movement speed, capped at 20px per frame
                    this.findParent().scrollLeft -= Math.min(tick - this.dragScrollLastTick, 20)
                        * this.dragScrollMultiplier
                }
                this.dragScrollLastTick = tick
                // repeat scroll attempt on next frame
                this.scrollLeft()
            })
        }
    }

    private findParent():HTMLElement {
        if (!this.pipelineEl || !this.pipelineEl.parentElement) {
            this.$error('Unable to access pipeline parent element', 'NO_PARENT')
            throw 'NO_PARENT'
        }
        return this.pipelineEl.parentElement
    }

    private scrollRight() {
        if (this.dragShouldScroll === 'right') {
            requestAnimationFrame((tick) => {
                if (this.dragScrollLastTick) {
                    // we're aiming for 1000px/second * multiplier movement speed, capped at 20px per frame
                    this.findParent().scrollLeft += Math.min(tick - this.dragScrollLastTick, 20)
                        * this.dragScrollMultiplier
                }
                this.dragScrollLastTick = tick
                // repeat scroll attempt on next frame
                this.scrollRight()
            })
        }
    }

    private shouldScrollLeft(event: PointerEvent) {
        // move when left boundary of parent is closer to phantom's left side than the drag threshold
        return Math.abs(event.pageX - this.findParent().getBoundingClientRect().left)
            < this.dragEdgeThreshold + this.dragStartX
    }

    private shouldScrollRight(event: PointerEvent) {
        // move when right boundary of parent is closer to phantom's right side than the drag threshold
        return Math.abs(event.pageX - this.findParent().getBoundingClientRect().right)
            < this.dragEdgeThreshold + this.dragObjectWidth - this.dragStartX
    }
}
